// #region Imports

/* Telestream */
import {
  SET_SCOPES_CAN_PLAY,
  SET_SCOPES_CONTROLLER,
  SET_SCOPES_ELEMENT,
  SET_SCOPES_GRATICULE,
  SET_SCOPES_TYPE
} from '@/utils/mutation-types';
import {
  ATTACH_VIDEO_ELEMENT,
  PAUSE_VIDEO,
  PLAY_VIDEO,
  SAVE_SCOPE_IMAGE,
  START_SCOPES,
  STOP_SCOPES
} from '@/utils/action-types';
import { generateController, fetchVideoScopeGraticule } from './utilities/media.utils';
import { splitPath } from '@/components/file-toolbar/utilities/path.utils';

// #endregion Imports

// #region Constants

// How much the media can drift (in seconds) before readjusting.
const MEDIA_DRIFT_EPSILON = 0.01;

// #endregion Constants

export const scopes = {

  // #region State

  state: {
    element:    undefined,
    controller: undefined,
    canPlay:    false,
    type:       'ComponentParade',
    graticule:  undefined
  },

  // #endregion State

  // #region Mutations

  mutations: {

    [SET_SCOPES_CAN_PLAY](state, canPlay) {
      state.canPlay = canPlay;
    },

    [SET_SCOPES_CONTROLLER](state, controller) {
      state.controller = controller;
    },

    [SET_SCOPES_ELEMENT](state, element) {
      state.element = element;
    },

    [SET_SCOPES_GRATICULE](state, graticule) {
      state.graticule = graticule;
    },

    [SET_SCOPES_TYPE](state, type) {
      state.type = type;
    }

  },

  // #endregion Mutations

  // #region Actions

  actions: {

    [ATTACH_VIDEO_ELEMENT](context, element) {
      /**
       * Sync the time between the media and the scope. Updating using a requestAnimationFrame
       * loop proved to be too fast and cause stuttering issues in the scope.
       */
      element.ontimeupdate = () => {
        if (!context.state.element) {
          return;
        }

        /*
         * Instead of updating on every change, the scope's current time is only
         * adjusted after a certain amount of drift. This makes the scope playback
         * smoother and makes it easier to debug timing issues.
         */

        const mediaTime = element.currentTime;
        const scopeTime = context.state.element.currentTime;
        if (Math.abs(mediaTime - scopeTime) > MEDIA_DRIFT_EPSILON) {
          context.state.element.currentTime = mediaTime;
        }
      };
    },

    async [START_SCOPES](context, type) {
      if (
        !context.rootState.videoController ||
        !context.state.element
      ) {
        return;
      }

      // Element
      context.state.element.currentTime = context.rootState.video.element.currentTime;
      context.commit(SET_SCOPES_ELEMENT, context.state.element);

      context.state.element.addEventListener(
        'canplay',
        () => {
          context.commit(SET_SCOPES_CAN_PLAY, true);
          if (!context.rootState.video.element.paused) {
            context.state.element.play();
          }
        },
        { once: true }
      );

      // Controller
      const quality = {
        currentLevel:     type,
        drawAlphaChannel: false
      };

      const url = context.rootState.videoController.masterManifestUrl + '&videoScope=' + quality.currentLevel;

      const scopeGraticuleString = await fetchVideoScopeGraticule(new URL(url).origin, context.rootState.videoPath, context.rootState.audioPathsConfig, quality.currentLevel);
      const scopeGraticule = JSON.parse(scopeGraticuleString);

      context.commit(SET_SCOPES_GRATICULE, scopeGraticule);
      context.commit(SET_SCOPES_TYPE, type);


      /*
       * The scope video does not resize correctly on Safari if we use HLS.js to
       * display it. Fortunately, Safari supports HLS video natively, and we don't
       * need any of the additional HLS.js features, so for scopes we just bypass it. 
       */

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (!isSafari) {
        const mediaProperties = context.rootState.video.properties;
        const controller = generateController(context.state.element, url, mediaProperties, quality);
        controller.init();

        context.commit(SET_SCOPES_CONTROLLER, controller);
      } else {
        context.state.element.src = url;
      }
    },

    [STOP_SCOPES](context) {
      if (context.state.element) {
        context.state.element.pause();
      }

      context.commit(SET_SCOPES_CAN_PLAY, false);
      context.rootState.licenseService.closeSession(context.rootState.videoPath, context.state.type);

      if (!context.state.controller) {
        return;
      }

      context.state.controller.destroy();
      context.commit(SET_SCOPES_CONTROLLER, undefined);
    },

    [PLAY_VIDEO](context) {
      if (context.state.element) {
        context.state.element.play();
      }
    },

    [PAUSE_VIDEO](context) {
      if (context.state.element) {
        context.state.element.pause();
      }
    },

    [SAVE_SCOPE_IMAGE](context) {
      const url = `${window.location.origin}/play/clip.jpeg?` +
                 `path=${encodeURIComponent(context.rootState.videoPath)}` +
                 `&videoscope=${context.state.type}` +
                 `&startFrame=${context.rootState.video.currentFrame}`;

      const filename = splitPath(context.rootState.videoPath)
        .filename
        .replaceAll(' ', '')
        .split('.')[0];

      const link = document.createElement('a');
      link.download = `${filename}_${context.state.type}_Scope`;
      link.href = url;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // #endregion Actions

}